<template>
  <div class="min-h-screen flex flex-col relative overflow-hidden bg-nirvana-dark">
    <!-- ═══ AMBIENT BACKGROUND EFFECTS ═══ -->
    <div class="fixed inset-0 pointer-events-none overflow-hidden">
      <!-- Large ambient glow orbs -->
      <div class="glow-orb w-[600px] h-[600px] -top-40 -left-40 animate-glow-pulse"
           style="background: radial-gradient(circle, rgba(123, 47, 242, 0.08), transparent 70%);"></div>
      <div class="glow-orb w-[500px] h-[500px] top-1/3 -right-40 animate-glow-pulse"
           style="background: radial-gradient(circle, rgba(79, 209, 255, 0.06), transparent 70%); animation-delay: 2s;"></div>
      <div class="glow-orb w-[400px] h-[400px] bottom-0 left-1/3 animate-glow-pulse"
           style="background: radial-gradient(circle, rgba(232, 139, 199, 0.06), transparent 70%); animation-delay: 4s;"></div>

      <!-- Floating sparkle particles -->
      <template v-for="n in 20" :key="'sparkle-'+n">
        <div
          class="sparkle animate-sparkle"
          :style="{
            top: `${(n * 37 + 13) % 100}%`,
            left: `${(n * 53 + 7) % 100}%`,
            animationDelay: `${n * 0.4}s`,
            animationDuration: `${3 + (n % 4)}s`,
            background: n % 3 === 0
              ? 'rgba(232, 139, 199, 0.6)'
              : n % 3 === 1
                ? 'rgba(79, 209, 255, 0.6)'
                : 'rgba(123, 47, 242, 0.6)',
          }"
        ></div>
      </template>

      <!-- Swirling ornate rings (inspired by the second art image) -->
      <div class="absolute top-1/4 right-1/4 w-[300px] h-[300px] rounded-full border border-nirvana-purple/5 animate-lotus-spin"></div>
      <div class="absolute bottom-1/4 left-1/4 w-[200px] h-[200px] rounded-full border border-nirvana-lotus/5 animate-lotus-spin" style="animation-direction: reverse; animation-duration: 25s;"></div>
    </div>

    <!-- ═══ NAVIGATION ═══ -->
    <nav class="ornate-nav relative z-50">
      <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center h-16">
          <!-- Logo and Brand -->
          <router-link to="/" class="flex items-center space-x-3 group">
            <div class="relative">
              <img 
                class="h-9 w-9 sm:h-10 sm:w-10 rounded-full ring-1 ring-nirvana-cyan/20 group-hover:ring-nirvana-lotus/40 transition-all duration-500" 
                src="/nirvana-logo.png" 
                alt="Nirvana"
                loading="eager"
              >
              <div class="absolute -inset-1 rounded-full bg-nirvana-purple/10 blur-sm group-hover:bg-nirvana-lotus/15 transition-all duration-500"></div>
            </div>
            <span class="text-lg sm:text-xl font-semibold bg-gradient-to-r from-nirvana-cyan via-nirvana-lotus to-nirvana-cyan bg-clip-text text-transparent">
              Nirvana's Birthday
            </span>
          </router-link>
          
          <!-- Desktop Navigation -->
          <div class="hidden md:flex items-center space-x-2">
            <router-link
              v-for="link in navigation"
              :key="link.to"
              :to="link.to"
              :class="link.primary ? 'lotus-button text-sm py-2 px-5' : 'nav-link'"
            >
              {{ link.text }}
            </router-link>
          </div>

          <!-- Mobile Menu Button -->
          <button 
            @click="isMenuOpen = !isMenuOpen"
            class="md:hidden flex flex-col justify-center items-center h-10 w-10 rounded-lg border border-white/10 text-nirvana-cyan/70 gap-1.5 hover:border-nirvana-purple/30 transition-colors"
            aria-label="Toggle menu"
          >
            <span v-if="!isMenuOpen" class="w-5 h-0.5 bg-current transition-all"></span>
            <span v-if="!isMenuOpen" class="w-4 h-0.5 bg-current transition-all"></span>
            <span v-if="!isMenuOpen" class="w-5 h-0.5 bg-current transition-all"></span>
            <component v-else :is="XIcon" class="h-5 w-5" />
          </button>
        </div>
      </div>

      <!-- Mobile Menu -->
      <div 
        v-show="isMenuOpen" 
        class="md:hidden border-t border-white/5"
        style="background: rgba(5, 5, 16, 0.97);"
      >
        <div class="px-4 py-3 space-y-1">
          <router-link
            v-for="link in navigation"
            :key="link.to"
            :to="link.to"
            :class="link.primary ? 'lotus-button w-full justify-center text-sm' : 'nav-link block w-full'"
            @click="isMenuOpen = false"
          >
            {{ link.text }}
          </router-link>
        </div>
      </div>
    </nav>

    <!-- ═══ MAIN CONTENT ═══ -->
    <main class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex-grow w-full relative z-10">
      <router-view v-slot="{ Component, route }">
        <transition 
          name="page-transition" 
          mode="out-in"
          @before-leave="handleTransition"
          @after-enter="handleTransition"
        >
          <Suspense>
            <template #default>
              <component :is="Component" :key="route.path" />
            </template>
            <template #fallback>
              <div class="flex flex-col justify-center items-center min-h-[200px] gap-3">
                <div class="relative">
                  <div class="w-12 h-12 rounded-full border-2 border-transparent border-t-nirvana-cyan border-r-nirvana-purple animate-spin"></div>
                  <div class="absolute inset-2 rounded-full border border-transparent border-b-nirvana-lotus animate-spin" style="animation-direction: reverse; animation-duration: 1.5s;"></div>
                </div>
                <span class="text-nirvana-silver/40 text-sm">กำลังโหลด...</span>
              </div>
            </template>
          </Suspense>
        </transition>
      </router-view>
    </main>

    <!-- ═══ FOOTER ═══ -->
    <footer class="relative z-10 mt-auto">
      <div class="ornate-divider"></div>
      <div class="bg-nirvana-dark/80 backdrop-blur-md py-8">
        <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div class="text-center space-y-4">
            <!-- Lotus icon -->
            <div class="flex justify-center">
              <svg class="w-6 h-6 text-nirvana-lotus/30" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C12 2 8 6 8 10C8 12 9.5 13.5 12 14C14.5 13.5 16 12 16 10C16 6 12 2 12 2Z"/>
                <path d="M12 14C9 14 6 11 5 8C4 11 5 15 8 17C9.5 18 11 18 12 18C13 18 14.5 18 16 17C19 15 20 11 19 8C18 11 15 14 12 14Z" opacity="0.6"/>
              </svg>
            </div>

            <div class="flex justify-center space-x-5">
              <a
                v-for="social in socials"
                :key="social.url"
                :href="social.url"
                target="_blank"
                rel="noopener noreferrer"
                class="text-nirvana-silver/30 hover:text-nirvana-cyan transition-colors duration-300"
              >
                <component :is="social.icon" class="h-5 w-5" />
              </a>
            </div>
            <p class="text-xs text-nirvana-silver/20">© {{ currentYear }} Handlers Group — Made with 💜 for Nirvana</p>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { 
  TwitterIcon, 
  InstagramIcon, 
  YoutubeIcon,
  XIcon 
} from 'lucide-vue-next'
import DiscordIcon from './components/DiscordIcon.vue'

const isMenuOpen = ref(false)
const currentYear = computed(() => new Date().getFullYear())

const navigation = [
  { to: '/', text: 'หน้าหลัก' },
  { to: '/submit-wish', text: '✨ ส่งคำอวยพร', primary: true },
  { to: '/developers', text: 'ผู้พัฒนา' }
]

const socials = [
  { icon: DiscordIcon, url: 'https://discord.gg/MhWjZc4raY' },
  { icon: TwitterIcon, url: 'https://twitter.com/Nirvana_ARP' },
  { icon: InstagramIcon, url: 'https://instagram.com/nirvana_arp' },
  { icon: YoutubeIcon, url: 'https://youtube.com/@Nirvana_ARP' }
]

const handleTransition = () => {
  isMenuOpen.value = false
}
</script>
